import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Box, Center, Spinner } from 'quotient';

import { NavigationMainSection } from './NavigationMainSection';
import { NavigationSecondarySection } from './NavigationSecondarySection';
import { NavigationItemType, NavigationItem, MultiNavigationPanelProps } from './types';
import { findMatchingNavigationItems, hasSubComponent, hasSubItems } from './utils/navigation-utils';

export const MultiNavigationPanel: React.FC<MultiNavigationPanelProps> = ({
  navigationConfig,
  className,
  onItemSelect,
  onSubItemSelect,
  shouldAutoSelectSubItem = true,
  useReplaceNavigation = false,
  preserveQueryParamsForSubItems = true,
  isLoadingNavigationItems = false,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState<NavigationItemType | null>(null);
  const [hasSecondaryContent, setHasSecondaryContent] = useState<boolean>(false);
  const [secondaryItems, setSecondaryItems] = useState<NavigationItem[]>([]);
  const [selectedSecondaryItem, setSelectedSecondaryItem] = useState<string | null>(null);

  const navigateToPath = useCallback(
    (path: string, preserveQueryParams = false) => {
      let targetPath = path;

      // Preserve query parameters if enabled
      if (preserveQueryParams && location.search) {
        targetPath = `${path}${location.search}`;
      }

      if (useReplaceNavigation) {
        history.replace(targetPath);
      } else {
        history.push(targetPath);
      }
    },
    [useReplaceNavigation, location.search, history],
  );

  // Helper function to update the content that goes in the secondary section based on the selected item
  const updateSecondaryContent = useCallback(
    (activeItem: NavigationItemType) => {
      if (hasSubItems(activeItem) && activeItem.subItems) {
        setHasSecondaryContent(true);
        setSecondaryItems(activeItem.subItems);
        // Pre-select the first subitem
        if (activeItem.subItems.length > 0 && shouldAutoSelectSubItem) {
          setSelectedSecondaryItem(activeItem.subItems[0].id);
        } else {
          setSelectedSecondaryItem(null);
        }
      } else if (hasSubComponent(activeItem)) {
        // If it has a subComponent, we want to show the secondary section
        // but with no items, as the subComponent will be rendered there
        setSecondaryItems([]);
        setHasSecondaryContent(true);
        setSelectedSecondaryItem(null);
      } else {
        setSecondaryItems([]);
        setHasSecondaryContent(false);
        setSelectedSecondaryItem(null);
      }
    },
    [shouldAutoSelectSubItem],
  );

  // Initialize with first item selected
  useEffect(() => {
    if (isLoadingNavigationItems) {
      return;
    }

    if (navigationConfig.items.length > 0) {
      const firstItem = navigationConfig.items[0];

      // Check if current path matches any item in the new config
      const { matchedItem } = findMatchingNavigationItems(location.pathname, navigationConfig);

      // If no match found, navigate to the first item
      if (!matchedItem && firstItem.path) {
        navigateToPath(firstItem.path);
      } else {
        // Just update the selection state without navigating
        setSelectedItem(firstItem);
        updateSecondaryContent(firstItem);

        // Call the callback if provided
        onItemSelect?.(firstItem.id);
      }
    }
  }, [
    navigationConfig,
    onItemSelect,
    location.pathname,
    navigateToPath,
    updateSecondaryContent,
    shouldAutoSelectSubItem,
    isLoadingNavigationItems,
  ]);

  // Update selection based on current route
  useEffect(() => {
    if (isLoadingNavigationItems) {
      return;
    }

    const { matchedItem, matchedSecondaryItem } = findMatchingNavigationItems(location.pathname, navigationConfig);

    if (matchedItem) {
      setSelectedItem(matchedItem);
      updateSecondaryContent(matchedItem);

      if (matchedSecondaryItem) {
        setSelectedSecondaryItem(matchedSecondaryItem);
      }

      // If we matched a main item but no secondary item, and the main item has sub-items,
      // auto-redirect to the first sub-item
      if (
        !matchedSecondaryItem &&
        hasSubItems(matchedItem) &&
        matchedItem.subItems &&
        matchedItem.subItems.length > 0 &&
        shouldAutoSelectSubItem
      ) {
        const firstSubItem = matchedItem.subItems[0];
        // Only redirect if current path exactly matches the main item path
        if (matchedItem.path && location.pathname === matchedItem.path && firstSubItem.path) {
          navigateToPath(firstSubItem.path, preserveQueryParamsForSubItems);
        }
      }
    }
  }, [
    location.pathname,
    navigationConfig,
    navigateToPath,
    updateSecondaryContent,
    shouldAutoSelectSubItem,
    isLoadingNavigationItems,
    preserveQueryParamsForSubItems,
  ]);

  const handleItemClick = (item: NavigationItemType) => {
    setSelectedItem(item);
    updateSecondaryContent(item);

    // Call the callback if provided
    onItemSelect?.(item.id);

    if (item.path) {
      // Check if we're currently in a sub-item of this main item
      // If so, preserve query params when navigating back to the main item
      const isCurrentlyInSubItem = location.pathname.startsWith(`${item.path}/`);
      navigateToPath(item.path, isCurrentlyInSubItem ? preserveQueryParamsForSubItems : false);
    }
  };

  const handleSubItemClick = (subItem: NavigationItem) => {
    setSelectedSecondaryItem(subItem.id);

    // Call the callback if provided
    onSubItemSelect?.(subItem.id);

    if (subItem.path) {
      navigateToPath(subItem.path, preserveQueryParamsForSubItems);
    }
  };

  if (isLoadingNavigationItems) {
    // Display a loading screen if the navigation items are still being fetched
    return (
      <Center h="100%">
        <Spinner />
      </Center>
    );
  }

  return (
    <Box bg="white" className={`${className} navigation-panel`} display="flex" flexDirection="column" h="100%">
      <NavigationMainSection
        hasSecondaryContent={hasSecondaryContent}
        section={navigationConfig}
        selectedItem={selectedItem}
        onItemClick={handleItemClick}
      />
      <NavigationSecondarySection
        secondaryItems={secondaryItems}
        selectedItem={selectedItem}
        selectedSecondaryItem={selectedSecondaryItem}
        onSubItemClick={handleSubItemClick}
      />
    </Box>
  );
};

export default MultiNavigationPanel;
